import { Component, OnInit, OnDestroy, NgZone, HostListener, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { LeafletModule } from '@bluehalo/ngx-leaflet';
import * as L from 'leaflet';

// Fix for markerClusterGroup not being a function on L in some build environments
import 'leaflet.markercluster';
import type { GeoJsonObject } from 'geojson';

import {
  OutdoorMapService,
  OutdoorSession,
  OutdoorSessionDetail,
  FitActivity,
  FitActivityDetail,
  OutdoorNote,
  Photo,
  TrackPoint,
  CoverageTrack,
  Jagdgebiet,
} from './outdoor-map.service';

type PanelMode = 'hunting' | 'fitness' | 'notes' | 'photos' | 'coverage' | 'jagd';

// Palette for handheld tracks (multiple per session possible)
const HANDHELD_COLORS = ['#2563eb', '#7c3aed', '#0891b2', '#0d9488', '#1d4ed8'];
const DOG_COLOR = '#dc2626';
const SYNC_DOG_COLOR = '#ff6b6b';
const SYNC_HANDHELD_COLOR = '#60a5fa';

// Escapes user-editable text before it's interpolated into Leaflet popup HTML strings.
function escapeHtml(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

interface LayerState {
  shownSessionIds: string[];
  shownFitIds: string[];
  notesVisible: boolean;
  photosVisible: boolean;
  coverageVisible: boolean;
  shownJagdIds: number[];
}

@Component({
  selector: 'app-outdoor-map',
  standalone: true,
  imports: [CommonModule, FormsModule, LeafletModule],
  templateUrl: './outdoor-map.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './outdoor-map.component.scss',
})
export class OutdoorMapComponent implements OnInit, OnDestroy {

  // ── Map ───────────────────────────────────────────────────────────────────

  map: L.Map | null = null;

  mapOptions: L.MapOptions = {
    layers: [
      L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
        attribution: '© <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>',
        maxZoom: 19,
      }),
    ],
    zoom: 13,
    center: L.latLng(46.8, 13.5),
    zoomControl: false,   // re-added at topright in onMapReady
  };

  // Per-session / per-activity layer storage
  private sessionLayersMap  = new Map<string, L.Layer[]>();
  private fitLayersMap      = new Map<string, L.Layer[]>();

  private syncMarkers: L.CircleMarker[] = [];
  private noteMarkers: Map<number, L.Marker> = new Map();
  private photoClusterGroup: L.MarkerClusterGroup | null = null;
  private photoBlobUrls: string[] = [];  // tracked for revocation on cleanup

  // ── Location ──────────────────────────────────────────────────────────────

  locationEnabled = false;
  locationError: string | null = null;
  private locationWatchId: number | null = null;
  private locationMarker: L.CircleMarker | null = null;
  private locationAccuracyCircle: L.Circle | null = null;
  private lastKnownPosition: GeolocationCoordinates | null = null;

  // ── Panel ─────────────────────────────────────────────────────────────────

  panelOpen = true;
  mode: PanelMode = 'hunting';
  sidebarWidth = 320;  // pixels; variable via drag, persisted in localStorage

  private resizing = false;
  private resizeStartX = 0;
  private resizeStartWidth = 0;

  // ── Hunting ───────────────────────────────────────────────────────────────

  sessions: OutdoorSession[] = [];
  loadingSessions = false;
  loadingSession = false;
  loadingSessionId: string | null = null;

  // Which sessions are currently on the map
  shownSessionIds = new Set<string>();
  private shownSessionDetails = new Map<string, OutdoorSessionDetail>();

  // The session whose trim/sync sliders are active (most-recently toggled on)
  activeSession: OutdoorSessionDetail | null = null;

  // Full track data for the active session (for timelineVisible + initTimeline)
  dogPoints: TrackPoint[] = [];
  handheldPoints: TrackPoint[] = [];
  // Currently rendered (trimmed) data — used for sync interpolation
  private renderedDogPoints: TrackPoint[] = [];
  private renderedHandheldPoints: TrackPoint[] = [];

  // Timeline (sync position scrubber)
  timelineCurrent = 0;
  get timelineVisible(): boolean {
    return this.dogPoints.length > 0 && this.handheldPoints.length > 0;
  }
  get currentSyncDate(): Date | null {
    if (!this.timelineVisible) {
      return null;
    }
    return new Date(this.timelineMinMs + (this.timelineMaxMs - this.timelineMinMs) * this.timelineCurrent / 1000);
  }
  private timelineMinMs = 0;
  private timelineMaxMs = 0;

  // Trim (cut noisy start/end of tracks)
  trimStart = 0;   // 0–1000
  trimEnd   = 1000;
  get trimStartMs(): number {
    return this.timelineMinMs + (this.timelineMaxMs - this.timelineMinMs) * this.trimStart / 1000;
  }
  get trimEndMs(): number {
    return this.timelineMinMs + (this.timelineMaxMs - this.timelineMinMs) * this.trimEnd / 1000;
  }
  get trimStartDate(): Date | null {
    return this.timelineMaxMs > 0 ? new Date(this.trimStartMs) : null;
  }
  get trimEndDate(): Date | null {
    return this.timelineMaxMs > 0 ? new Date(this.trimEndMs) : null;
  }
  // CSS left/width for the trim fill bar
  get trimFillLeft(): number  { return this.trimStart / 10; }
  get trimFillWidth(): number { return (this.trimEnd - this.trimStart) / 10; }

  // ── Fitness ───────────────────────────────────────────────────────────────

  fitActivities: FitActivity[] = [];
  loadingFit = false;
  loadingFitDetail = false;

  // Which fit activities are currently on the map
  shownFitIds = new Set<string>();
  private shownFitDetails = new Map<string, FitActivityDetail>();

  // The activity whose scrubber is active (most-recently toggled on)
  activeFitActivity: FitActivityDetail | null = null;

  private renderedFitPoints: TrackPoint[] = [];
  private fitSyncMarker: L.CircleMarker | null = null;

  fitTimelineCurrent = 0;
  private fitTimelineMinMs = 0;
  private fitTimelineMaxMs = 0;

  get fitTimelineVisible(): boolean { return !!this.activeFitActivity && this.renderedFitPoints.length > 0; }
  get fitCurrentDate(): Date | null {
    if (!this.fitTimelineVisible) {
      return null;
    }
    return new Date(this.fitTimelineMinMs + (this.fitTimelineMaxMs - this.fitTimelineMinMs) * this.fitTimelineCurrent / 1000);
  }

  // ── Notes ─────────────────────────────────────────────────────────────────

  notes: OutdoorNote[] = [];
  loadingNotes = false;
  notesLoaded = false;
  notesVisible = false;
  editingNote: OutdoorNote | null = null;
  addingNote = false;  // true while waiting for map click to place new note
  findingJagdgebiet = false;  // true while waiting for map click to pick a point

  // ── Photos ────────────────────────────────────────────────────────────────

  photos: Photo[] = [];
  loadingPhotos = false;
  photosLoaded = false;
  photosVisible = false;
  editingPhotoPosition: Photo | null = null;

  // Full-size lightbox
  viewingPhoto: Photo | null = null;
  viewingPhotoBlobUrl: string | null = null;
  viewingPhotoLoading = false;

  // ── Coverage ──────────────────────────────────────────────────────────────

  coverageTrackCount = 0;
  coverageLoaded = false;
  loadingCoverage = false;
  coverageVisible = false;  // off by default; restored from localStorage

  // Configurable settings — persisted in localStorage
  coverageContrast      = 1.0;   // exponent after log: low=flat, high=steep
  coverageMinBrightness = 0.20;  // alpha of a cell visited just once (0–0.5)
  coverageHalfLife      = 24;    // temporal decay half-life in months (1–60)
  coverageKernelRadius  = 100;   // influence radius per track point in metres (20–300)
  coveragePalette       = 'plasma'; // color palette key

  private readonly COVERAGE_PALETTES: Record<string, [number, [number, number, number]][]> = {
    weather: [
      [0.0, [  0,   0, 255]],
      [0.2, [  0, 255, 255]],
      [0.4, [  0, 220,   0]],
      [0.6, [255, 255,   0]],
      [0.8, [255, 140,   0]],
      [1.0, [220,   0,   0]],
    ],
    fire: [
      [0.0,  [255, 255, 160]],  // pale yellow  — barely visited
      [0.20, [255, 220,   0]],  // yellow
      [0.50, [255, 130,   0]],  // orange
      [0.80, [210,  20,   0]],  // red
      [1.0,  [100,   0,   0]],  // dark red      — most visited
    ],
    plasma: [
      [0.0,  [240, 249,  33]],  // pale yellow   — barely visited
      [0.25, [248, 149,  64]],  // orange
      [0.5,  [204,  71, 120]],  // pink/magenta
      [0.75, [126,   3, 168]],  // purple
      [1.0,  [ 13,   8, 135]],  // dark purple   — most visited
    ],
    magma: [
      [0.0,  [252, 240, 180]],  // pale cream    — barely visited
      [0.25, [248, 140,  60]],  // orange
      [0.5,  [200,  30,  90]],  // red-pink
      [0.75, [100,   0, 130]],  // purple
      [1.0,  [ 40,   0,  80]],  // deep purple   — most visited
    ],
    purplered: [
      [0.0,  [255, 120,  40]],  // orange        — barely visited
      [0.33, [230,   0,  80]],  // crimson
      [0.67, [160,   0, 180]],  // magenta
      [1.0,  [ 50,   0, 130]],  // deep purple   — most visited
    ],
  };

  private rawCoverageTracks: CoverageTrack[] = [];
  private coverageCells: Array<{ lat: number; lon: number; intensity: number }> = [];
  private coverageOverlay: L.ImageOverlay | null = null;

  // ── Jagdgebiete ───────────────────────────────────────────────────────────

  jagdgebiete: Jagdgebiet[] = [];
  loadingJagd = false;
  jagdLoaded = false;
  shownJagdIds = new Set<number>();
  private jagdLayersMap = new Map<number, L.Layer[]>();

  constructor(private readonly svc: OutdoorMapService, private readonly zone: NgZone) {}

  ngOnInit(): void {
    const storedWidth = localStorage.getItem('sidebar-width');
    if (storedWidth) {
      this.sidebarWidth = Math.max(180, Math.min(700, +storedWidth));
    }
    const storedTab = localStorage.getItem('outdoor-tab') as PanelMode | null;
    if (storedTab) {
      this.mode = storedTab;
    }
    this.loadCoverageSettings();
    const state = this.readLayerState();
    if (state) {
      this.notesVisible    = state.notesVisible;
      this.photosVisible   = state.photosVisible;
      this.coverageVisible = state.coverageVisible;
    }

    // Always load both lists upfront so restoration can match IDs
    this.loadSessions(state?.shownSessionIds ?? []);
    this.loadFitActivities(state?.shownFitIds ?? []);

    // Eagerly load notes/photos if they were visible last time
    if (this.notesVisible) {
      this.loadNotes();
    }
    if (this.photosVisible) {
      this.loadPhotos();
    }

    // Eagerly load coverage if it was visible last time
    if (this.coverageVisible) {
      this.loadCoverage();
    }

    // Eagerly load jagdgebiete if any were shown last time
    const restoredJagdIds = state?.shownJagdIds ?? [];
    if (restoredJagdIds.length > 0) {
      this.loadJagdgebiete(restoredJagdIds);
    }
  }

  ngOnDestroy(): void {
    this.photoBlobUrls.forEach(u => URL.revokeObjectURL(u));
    if (this.viewingPhotoBlobUrl) {
      URL.revokeObjectURL(this.viewingPhotoBlobUrl);
    }
    if (this.photoClusterGroup && this.map) {
      this.map.removeLayer(this.photoClusterGroup);
    }
    this.stopLocation();
    this.detachCoverageOverlay();
    for (const layers of this.jagdLayersMap.values()) {
      layers.forEach(l => this.map?.removeLayer(l));
    }
    this.map = null;
  }

  @HostListener('document:keydown.escape')
  onEscape(): void {
    if (this.viewingPhoto) {
      this.closePhotoViewer();
    } else if (this.map) {
      this.map.closePopup();
    }
  }

  onResizeHandleMousedown(event: MouseEvent): void {
    this.resizing = true;
    this.resizeStartX = event.clientX;
    this.resizeStartWidth = this.sidebarWidth;
    event.preventDefault();
  }

  @HostListener('document:mousemove', ['$event'])
  onDocMouseMove(event: MouseEvent): void {
    if (!this.resizing) {
      return;
    }
    const delta = event.clientX - this.resizeStartX;
    this.sidebarWidth = Math.max(180, Math.min(700, this.resizeStartWidth + delta));
  }

  @HostListener('document:mouseup')
  onDocMouseUp(): void {
    if (!this.resizing) {
      return;
    }
    this.resizing = false;
    localStorage.setItem('sidebar-width', String(this.sidebarWidth));
    this.map?.invalidateSize();
  }

  // ── Location ──────────────────────────────────────────────────────────────

  toggleLocation(): void {
    if (this.locationEnabled) {
      this.stopLocation();
    } else {
      this.startLocation();
    }
  }

  private startLocation(): void {
    if (!navigator.geolocation) {
      this.locationError = 'Geolocation not supported';
      return;
    }
    this.locationError = null;
    this.locationEnabled = true;
    this.closeOnMobile();
    this.locationWatchId = navigator.geolocation.watchPosition(
      pos => this.zone.run(() => this.onLocationUpdate(pos)),
      err => this.zone.run(() => {
        this.locationError = err.code === 1 ? 'Location access denied' : 'Location unavailable';
        this.stopLocation();
      }),
      { enableHighAccuracy: true, maximumAge: 5000 },
    );
  }

  private stopLocation(): void {
    if (this.locationWatchId != null) {
      navigator.geolocation.clearWatch(this.locationWatchId);
      this.locationWatchId = null;
    }
    if (this.map) {
      if (this.locationMarker) {
        this.map.removeLayer(this.locationMarker);
        this.locationMarker = null;
      }
      if (this.locationAccuracyCircle) {
        this.map.removeLayer(this.locationAccuracyCircle);
        this.locationAccuracyCircle = null;
      }
    }
    this.locationEnabled = false;
  }

  private onLocationUpdate(pos: GeolocationPosition): void {
    if (!this.map) {
      return;
    }
    const { latitude: lat, longitude: lng, accuracy } = pos.coords;
    this.lastKnownPosition = pos.coords;
    const latlng = L.latLng(lat, lng);

    if (!this.locationMarker) {
      this.locationMarker = L.circleMarker(latlng, {
        radius: 8, color: '#fff', weight: 2,
        fillColor: '#2563eb', fillOpacity: 1,
        pane: 'markerPane',
      }).addTo(this.map);
      this.locationAccuracyCircle = L.circle(latlng, {
        radius: accuracy, color: '#2563eb', weight: 1,
        fillColor: '#2563eb', fillOpacity: 0.10,
        interactive: false,
      }).addTo(this.map);
      // First fix → pan to position without changing zoom
      this.map.panTo(latlng);
    } else {
      this.locationMarker.setLatLng(latlng);
      this.locationAccuracyCircle?.setLatLng(latlng);
      this.locationAccuracyCircle?.setRadius(accuracy);
    }
  }

  onMapReady(map: L.Map): void {
    this.map = map;
    L.control.zoom({ position: 'topright' }).addTo(map);
    map.on('click', (e: L.LeafletMouseEvent) => this.zone.run(() => this.onMapClick(e)));

    // Restore saved map position/zoom
    try {
      const raw = localStorage.getItem('outdoor-map-view');
      if (raw) {
        const { lat, lng, zoom } = JSON.parse(raw);
        map.setView([lat, lng], zoom, { animate: false });
      }
    } catch { /* ignore */ }

    // Save position/zoom on every pan/zoom end
    const saveView = () => {
      const c = map.getCenter();
      localStorage.setItem('outdoor-map-view', JSON.stringify({ lat: c.lat, lng: c.lng, zoom: map.getZoom() }));
    };
    map.on('moveend', saveView);
    map.on('zoomend', saveView);
  }

  togglePanel(): void {
    this.panelOpen = !this.panelOpen;
    setTimeout(() => this.map?.invalidateSize(), 310);
  }

  switchMode(m: PanelMode): void {
    this.mode = m;
    localStorage.setItem('outdoor-tab', m);
    if (m === 'fitness' && this.fitActivities.length === 0 && !this.loadingFit) {
      this.loadFitActivities([]);
    }
    if (m === 'notes' && !this.notesLoaded) {
      this.loadNotes();
    }
    if (m === 'photos' && !this.photosLoaded) {
      this.loadPhotos();
    }
    if (m === 'coverage' && !this.coverageLoaded) {
      this.loadCoverage();
    }
    if (m === 'jagd' && !this.jagdLoaded) {
      this.loadJagdgebiete([]);
    }
  }

  // ── Layer state persistence ───────────────────────────────────────────────

  private saveLayerState(): void {
    const state: LayerState = {
      shownSessionIds: [...this.shownSessionIds],
      shownFitIds:     [...this.shownFitIds],
      notesVisible:    this.notesVisible,
      photosVisible:   this.photosVisible,
      coverageVisible: this.coverageVisible,
      shownJagdIds:    [...this.shownJagdIds],
    };
    localStorage.setItem('outdoor-layer-state', JSON.stringify(state));
  }

  private readLayerState(): LayerState | null {
    try {
      const raw = localStorage.getItem('outdoor-layer-state');
      return raw ? (JSON.parse(raw) as LayerState) : null;
    } catch { return null; }
  }

  // ── Clear all ─────────────────────────────────────────────────────────────

  clearAll(): void {
    const map = this.map;
    if (!map) {
      return;
    }

    // Remove all session layers
    for (const layers of this.sessionLayersMap.values()) {
      layers.forEach(l => map.removeLayer(l));
    }
    this.sessionLayersMap.clear();
    this.shownSessionIds.clear();
    this.shownSessionDetails.clear();
    this.activeSession = null;
    this.dogPoints = [];
    this.handheldPoints = [];
    this.renderedDogPoints = [];
    this.renderedHandheldPoints = [];
    this.syncMarkers.forEach(m => map.removeLayer(m));
    this.syncMarkers = [];
    this.trimStart = 0;
    this.trimEnd   = 1000;

    // Remove all fit activity layers
    for (const layers of this.fitLayersMap.values()) {
      layers.forEach(l => map.removeLayer(l));
    }
    this.fitLayersMap.clear();
    this.shownFitIds.clear();
    this.shownFitDetails.clear();
    this.activeFitActivity = null;
    this.renderedFitPoints = [];
    if (this.fitSyncMarker) {
      map.removeLayer(this.fitSyncMarker);
      this.fitSyncMarker = null;
    }
    this.fitTimelineCurrent = 0;

    // Hide notes
    if (this.notesVisible) {
      this.noteMarkers.forEach(m => map.removeLayer(m));
      this.notesVisible = false;
    }

    // Hide photos
    if (this.photosVisible && this.photoClusterGroup) {
      map.removeLayer(this.photoClusterGroup);
      this.photosVisible = false;
    }

    // Hide coverage
    if (this.coverageOverlay) {
      this.coverageOverlay.setOpacity(0);
      this.coverageVisible = false;
    }

    // Remove all jagdgebiet layers
    for (const layers of this.jagdLayersMap.values()) {
      layers.forEach(l => map.removeLayer(l));
    }
    this.jagdLayersMap.clear();
    this.shownJagdIds.clear();

    this.saveLayerState();
  }

  // ── Hunting ───────────────────────────────────────────────────────────────

  private loadSessions(restoreIds: string[]): void {
    this.loadingSessions = true;
    this.svc.listSessions().subscribe({
      next: sessions => {
        this.sessions = sessions;
        this.loadingSessions = false;
        // Restore previously shown sessions
        for (const id of restoreIds) {
          const s = sessions.find(x => x.id === id);
          if (s) {
            this.loadSession(s, false);
          }
        }
      },
      error: () => { this.loadingSessions = false; },
    });
  }

  loadSession(session: OutdoorSession, fitBounds = true): void {
    // Toggle off if already shown
    if (this.shownSessionIds.has(session.id)) {
      this.removeSession(session.id);
      return;
    }
    if (this.loadingSession) {
      return;
    }
    this.loadingSession = true;
    this.loadingSessionId = session.id;

    this.svc.getSessionDetail(session.id).subscribe({
      next: detail => {
        this.shownSessionIds.add(session.id);
        this.shownSessionDetails.set(session.id, detail);
        this.renderSessionTracks(detail, -Infinity, Infinity, fitBounds);
        this.setActiveSession(detail);
        this.loadingSession = false;
        this.loadingSessionId = null;
        this.saveLayerState();
      },
      error: () => {
        this.loadingSession = false;
        this.loadingSessionId = null;
      },
    });
  }

  private removeSession(id: string): void {
    const layers = this.sessionLayersMap.get(id) ?? [];
    const map = this.map;
    if (map) {
      layers.forEach(l => map.removeLayer(l));
    }
    this.sessionLayersMap.delete(id);
    this.shownSessionIds.delete(id);
    this.shownSessionDetails.delete(id);

    // If this was the active session, switch to the most recently added remaining session
    if (this.activeSession?.id === id) {
      const remaining = [...this.shownSessionDetails.values()];
      const next = remaining.length > 0 ? remaining[remaining.length - 1] : null;
      this.setActiveSession(next);
    }
    this.saveLayerState();
  }

  private setActiveSession(detail: OutdoorSessionDetail | null): void {
    this.activeSession = detail;
    if (detail) {
      const dogTrack      = detail.tracks.find(t => t.type === 'DOG');
      const handheldTrack = detail.tracks.find(t => t.type === 'HANDHELD');
      this.dogPoints      = dogTrack?.points      ?? [];
      this.handheldPoints = handheldTrack?.points ?? [];
      this.renderedDogPoints      = [...this.dogPoints];
      this.renderedHandheldPoints = [...this.handheldPoints];
      this.trimStart = 0;
      this.trimEnd   = 1000;
      this.initTimeline();
    } else {
      this.dogPoints = [];
      this.handheldPoints = [];
      this.renderedDogPoints = [];
      this.renderedHandheldPoints = [];
      this.syncMarkers.forEach(m => this.map?.removeLayer(m));
      this.syncMarkers = [];
      this.trimStart = 0;
      this.trimEnd   = 1000;
    }
  }

  // Trim slider handlers
  onTrimStartInput(event: Event): void {
    const val = +(event.target as HTMLInputElement).value;
    this.trimStart = Math.min(val, this.trimEnd - 10);
    (event.target as HTMLInputElement).value = String(this.trimStart);
    this.timelineCurrent = Math.max(this.timelineCurrent, this.trimStart);
    this.applyTrimAndRender();
  }

  onTrimEndInput(event: Event): void {
    const val = +(event.target as HTMLInputElement).value;
    this.trimEnd = Math.max(val, this.trimStart + 10);
    (event.target as HTMLInputElement).value = String(this.trimEnd);
    this.timelineCurrent = Math.min(this.timelineCurrent, this.trimEnd);
    this.applyTrimAndRender();
  }

  private applyTrimAndRender(): void {
    if (!this.activeSession || !this.map) {
      return;
    }
    const id = this.activeSession.id;
    const old = this.sessionLayersMap.get(id) ?? [];
    const map = this.map;
    if (!map) {
      return;
    }
    old.forEach(l => map.removeLayer(l));
    this.sessionLayersMap.set(id, []);
    this.renderSessionTracks(this.activeSession, this.trimStartMs, this.trimEndMs, false);
  }

  /**
   * Render session tracks filtered to [fromMs, toMs].
   * Stores resulting layers in sessionLayersMap keyed by detail.id.
   * When fitBounds=true (initial load): also calls initTimeline(), fits map bounds.
   * When fitBounds=false (trim change): only re-renders visible polylines.
   */
  private renderSessionTracks(
    detail: OutdoorSessionDetail,
    fromMs = -Infinity,
    toMs = Infinity,
    fitBounds = true,
  ): void {
    if (!this.map) {
      return;
    }
    const layers: L.Layer[] = [];
    const bounds: L.LatLng[] = [];
    let handheldIdx = 0;

    for (const track of detail.tracks) {
      const pts = (fromMs === -Infinity && toMs === Infinity)
        ? track.points
        : track.points.filter(p => {
            const t = new Date(p.time).getTime();
            return t >= fromMs && t <= toMs;
          });

      if (pts.length < 2) {
        continue;
      }

      const latlngs = pts.map(p => L.latLng(p.lat, p.lon));
      bounds.push(...latlngs);

      const color = track.type === 'DOG' ? DOG_COLOR : HANDHELD_COLORS[handheldIdx % HANDHELD_COLORS.length];
      if (track.type === 'HANDHELD') {
        handheldIdx++;
      }

      const visLine = L.polyline(latlngs, { color, weight: 3, opacity: 0.85, interactive: false });
      this.map.addLayer(visLine);
      layers.push(visLine);

      // Wide transparent hit area — captures mousemove within ~10 px of the line
      const hitLine = L.polyline(latlngs, { color, weight: 20, opacity: 0.001, interactive: true });
      hitLine.bindTooltip('', { sticky: true, opacity: 0.9 });

      // Capture for closure
      const capturedPts  = pts;
      const capturedName = track.name;

      hitLine.on('mousemove', (e: L.LeafletMouseEvent) => {
        const nearestMs = this.findNearestPointTime(capturedPts, e.latlng);
        if (nearestMs != null) {
          hitLine.getTooltip()?.setContent(`${capturedName}: ${this.formatPointTime(nearestMs)}`);
          this.updateSyncMarkers(nearestMs);
        }
      });
      hitLine.on('mouseout', () => this.updateSyncMarkersFromSlider());

      this.map.addLayer(hitLine);
      layers.push(hitLine);

      // start/end markers
      const startM = L.marker(latlngs[0],                  { icon: this.dotIcon(color, 8) }).bindPopup(`Start: ${track.name}`);
      const endM   = L.marker(latlngs[latlngs.length - 1], { icon: this.dotIcon(color, 8) }).bindPopup(`End: ${track.name}`);
      this.map.addLayer(startM);
      this.map.addLayer(endM);
      layers.push(startM, endM);
    }

    this.sessionLayersMap.set(detail.id, layers);

    // Update rendered (trimmed) points used by sync markers (only for active session)
    if (this.activeSession?.id === detail.id || fitBounds) {
      const filterPts = (p: TrackPoint[]) =>
        (fromMs === -Infinity && toMs === Infinity) ? p
          : p.filter(x => {
              const t = new Date(x.time).getTime();
              return t >= fromMs && t <= toMs;
            });

      const dogTrack      = detail.tracks.find(t => t.type === 'DOG');
      const handheldTrack = detail.tracks.find(t => t.type === 'HANDHELD');

      if (dogTrack) {
        this.renderedDogPoints = filterPts(dogTrack.points);
      }
      if (handheldTrack) {
        this.renderedHandheldPoints = filterPts(handheldTrack.points);
      }
    }

    if (fitBounds && bounds.length > 0) {
      this.map.fitBounds(L.latLngBounds(bounds), { padding: [20, 20] });
    }
  }

  // ── Timeline sync ─────────────────────────────────────────────────────────

  private initTimeline(): void {
    if (!this.timelineVisible) {
      return;
    }

    const allTimes = [
      ...this.dogPoints.map(p => new Date(p.time).getTime()),
      ...this.handheldPoints.map(p => new Date(p.time).getTime()),
    ];
    this.timelineMinMs = Math.min(...allTimes);
    this.timelineMaxMs = Math.max(...allTimes);
    this.timelineCurrent = 0;
    // Reset trim to full range on every new session load
    this.trimStart = 0;
    this.trimEnd   = 1000;
  }

  onTimelineInput(event: Event): void {
    this.timelineCurrent = +(event.target as HTMLInputElement).value;
    this.updateSyncMarkersFromSlider();
  }

  private updateSyncMarkersFromSlider(): void {
    const t = this.timelineMinMs + (this.timelineMaxMs - this.timelineMinMs) * this.timelineCurrent / 1000;
    this.updateSyncMarkers(t);
  }

  private updateSyncMarkers(timeMs: number): void {
    const map = this.map;
    if (!map) {
      return;
    }
    this.syncMarkers.forEach(m => map.removeLayer(m));
    this.syncMarkers = [];

    const dogPos  = this.interpolatePosition(this.renderedDogPoints,     timeMs);
    const heldPos = this.interpolatePosition(this.renderedHandheldPoints, timeMs);

    if (dogPos) {
      const m = L.circleMarker(dogPos, {
        radius: 8, color: '#fff', fillColor: SYNC_DOG_COLOR, fillOpacity: 1, weight: 2,
      }).bindTooltip('Dog position');
      this.map.addLayer(m);
      this.syncMarkers.push(m);
    }
    if (heldPos) {
      const m = L.circleMarker(heldPos, {
        radius: 8, color: '#fff', fillColor: SYNC_HANDHELD_COLOR, fillOpacity: 1, weight: 2,
      }).bindTooltip('Handheld position');
      this.map.addLayer(m);
      this.syncMarkers.push(m);
    }
    if (dogPos && heldPos) {
      const dist = this.haversineM(dogPos.lat, dogPos.lng, heldPos.lat, heldPos.lng);
      const line = L.polyline([dogPos, heldPos], {
        color: '#94a3b8', weight: 1, opacity: 0.6, dashArray: '4 4',
      }).bindTooltip(`Distance: ${Math.round(dist)} m`);
      this.map.addLayer(line);
      this.syncMarkers.push(line as unknown as L.CircleMarker);
    }
  }

  private interpolatePosition(points: TrackPoint[], timeMs: number): L.LatLng | null {
    if (points.length === 0) {
      return null;
    }
    const t0 = new Date(points[0].time).getTime();
    const tN = new Date(points[points.length - 1].time).getTime();
    if (timeMs <= t0) {
      return L.latLng(points[0].lat, points[0].lon);
    }
    if (timeMs >= tN) {
      return L.latLng(points[points.length - 1].lat, points[points.length - 1].lon);
    }

    // binary search
    let lo = 0, hi = points.length - 1;
    while (lo < hi - 1) {
      const mid = (lo + hi) >> 1;
      if (new Date(points[mid].time).getTime() <= timeMs) {
        lo = mid;
      } else {
        hi = mid;
      }
    }
    const p1 = points[lo], p2 = points[hi];
    const t1 = new Date(p1.time).getTime(), t2 = new Date(p2.time).getTime();
    const ratio = (timeMs - t1) / (t2 - t1);
    return L.latLng(p1.lat + (p2.lat - p1.lat) * ratio, p1.lon + (p2.lon - p1.lon) * ratio);
  }

  private haversineM(lat1: number, lon1: number, lat2: number, lon2: number): number {
    const R = 6_371_000;
    const dLat = (lat2 - lat1) * Math.PI / 180;
    const dLon = (lon2 - lon1) * Math.PI / 180;
    const a = Math.sin(dLat / 2) ** 2 +
              Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) * Math.sin(dLon / 2) ** 2;
    return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  }

  // ── Fitness ───────────────────────────────────────────────────────────────

  private loadFitActivities(restoreIds: string[]): void {
    this.loadingFit = true;
    this.svc.listFitActivities().subscribe({
      next: acts => {
        this.fitActivities = acts;
        this.loadingFit = false;
        // Restore previously shown activities
        for (const id of restoreIds) {
          const a = acts.find(x => x.id === id);
          if (a) {
            this.loadFitActivity(a, false);
          }
        }
      },
      error: () => { this.loadingFit = false; },
    });
  }

  enableAllFit(): void {
    for (const act of this.fitActivities) {
      if (this.shownFitIds.has(act.id)) {
        continue;
      }
      // Fetch in parallel — bypass the loadingFitDetail single-request guard
      this.svc.getFitActivityDetail(act.id).subscribe({
        next: detail => {
          this.shownFitIds.add(act.id);
          this.shownFitDetails.set(act.id, detail);
          this.renderFitTrack(detail, false);
          this.setActiveFitActivity(detail);
          this.saveLayerState();
        },
      });
    }
  }

  disableAllFit(): void {
    for (const id of [...this.shownFitIds]) {
      this.removeFitActivity(id);
    }
  }

  loadFitActivity(activity: FitActivity, fitBounds = true): void {
    // Toggle off if already shown
    if (this.shownFitIds.has(activity.id)) {
      this.removeFitActivity(activity.id);
      return;
    }
    if (this.loadingFitDetail) {
      return;
    }
    this.loadingFitDetail = true;

    this.svc.getFitActivityDetail(activity.id).subscribe({
      next: detail => {
        this.shownFitIds.add(activity.id);
        this.shownFitDetails.set(activity.id, detail);
        this.renderFitTrack(detail, fitBounds);
        this.setActiveFitActivity(detail);
        this.loadingFitDetail = false;
        this.saveLayerState();
      },
      error: () => { this.loadingFitDetail = false; },
    });
  }

  private removeFitActivity(id: string): void {
    const layers = this.fitLayersMap.get(id) ?? [];
    const map = this.map;
    if (map) {
      layers.forEach(l => map.removeLayer(l));
    }
    this.fitLayersMap.delete(id);
    this.shownFitIds.delete(id);
    this.shownFitDetails.delete(id);

    // If this was the active activity, switch to most-recently added remaining one
    if (this.activeFitActivity?.id === id) {
      const remaining = [...this.shownFitDetails.values()];
      const next = remaining.length > 0 ? remaining[remaining.length - 1] : null;
      this.setActiveFitActivity(next);
    }
    this.saveLayerState();
  }

  private setActiveFitActivity(detail: FitActivityDetail | null): void {
    this.activeFitActivity = detail;
    if (detail) {
      this.renderedFitPoints = detail.points;
      this.initFitTimeline();
    } else {
      this.renderedFitPoints = [];
      if (this.fitSyncMarker && this.map) {
        this.map.removeLayer(this.fitSyncMarker);
        this.fitSyncMarker = null;
      }
      this.fitTimelineCurrent = 0;
    }
  }

  private renderFitTrack(detail: FitActivityDetail, fitBounds = true): void {
    if (!this.map || detail.points.length === 0) {
      return;
    }
    const layers: L.Layer[] = [];

    const latlngs = detail.points.map(p => L.latLng(p.lat, p.lon));

    const visLine = L.polyline(latlngs, { color: '#7c3aed', weight: 3, opacity: 0.85, interactive: false });
    this.map.addLayer(visLine);
    layers.push(visLine);

    const hitLine = L.polyline(latlngs, { color: '#7c3aed', weight: 20, opacity: 0.001, interactive: true });
    hitLine.bindTooltip('', { sticky: true, opacity: 0.9 });

    const capturedPts  = detail.points;
    const capturedName = detail.name;

    hitLine.on('mousemove', (e: L.LeafletMouseEvent) => {
      const nearestMs = this.findNearestPointTime(capturedPts, e.latlng);
      if (nearestMs != null) {
        hitLine.getTooltip()?.setContent(`${capturedName}: ${this.formatPointTime(nearestMs)}`);
        this.updateFitSyncMarker(nearestMs);
      }
    });
    hitLine.on('mouseout', () => this.updateFitSyncMarkerFromSlider());

    this.map.addLayer(hitLine);
    layers.push(hitLine);

    this.fitLayersMap.set(detail.id, layers);

    if (fitBounds) {
      this.map.fitBounds(L.latLngBounds(latlngs), { padding: [20, 20] });
    }
  }

  private initFitTimeline(): void {
    if (this.renderedFitPoints.length === 0) {
      return;
    }
    const times = this.renderedFitPoints.map(p => new Date(p.time).getTime());
    this.fitTimelineMinMs = Math.min(...times);
    this.fitTimelineMaxMs = Math.max(...times);
    this.fitTimelineCurrent = 0;
  }

  onFitTimelineInput(event: Event): void {
    this.fitTimelineCurrent = +(event.target as HTMLInputElement).value;
    this.updateFitSyncMarkerFromSlider();
  }

  private updateFitSyncMarkerFromSlider(): void {
    const t = this.fitTimelineMinMs + (this.fitTimelineMaxMs - this.fitTimelineMinMs) * this.fitTimelineCurrent / 1000;
    this.updateFitSyncMarker(t);
  }

  private updateFitSyncMarker(timeMs: number): void {
    if (!this.map) {
      return;
    }
    if (this.fitSyncMarker) {
      this.map.removeLayer(this.fitSyncMarker);
      this.fitSyncMarker = null;
    }
    const pos = this.interpolatePosition(this.renderedFitPoints, timeMs);
    if (pos) {
      this.fitSyncMarker = L.circleMarker(pos, {
        radius: 8, color: '#fff', fillColor: '#7c3aed', fillOpacity: 1, weight: 2,
      });
      this.map.addLayer(this.fitSyncMarker);
    }
  }

  // ── Jagdgebiete ───────────────────────────────────────────────────────────

  private loadJagdgebiete(restoreIds: number[]): void {
    if (this.loadingJagd || this.jagdLoaded) {
      return;
    }
    this.loadingJagd = true;
    this.svc.listJagdgebiete().subscribe({
      next: areas => {
        this.jagdgebiete = areas;
        this.jagdLoaded = true;
        this.loadingJagd = false;
        for (const id of restoreIds) {
          const area = areas.find(a => a.id === id);
          if (area) {
            this.renderJagdgebiet(area);
          }
        }
      },
      error: () => { this.loadingJagd = false; },
    });
  }

  toggleJagdgebiet(area: Jagdgebiet): void {
    if (this.shownJagdIds.has(area.id)) {
      const layers = this.jagdLayersMap.get(area.id) ?? [];
      layers.forEach(l => this.map?.removeLayer(l));
      this.jagdLayersMap.delete(area.id);
      this.shownJagdIds.delete(area.id);
      this.saveLayerState();
    } else {
      // Mark as shown immediately so the badge appears; geom loads async
      this.shownJagdIds.add(area.id);
      this.saveLayerState();
      this.svc.getJagdgebietGeom(area.id).subscribe({
        next: geom => this.renderJagdgebietGeom(area, geom),
        error: () => {
          this.shownJagdIds.delete(area.id);
          this.saveLayerState();
        },
      });
    }
  }

  private renderJagdgebietGeom(area: Jagdgebiet, geom: string): void {
    if (!this.map) {
      return;
    }
    let geoJsonObj: GeoJsonObject;
    try {
      geoJsonObj = JSON.parse(geom);
    } catch (e) {
      console.error(`[Jagd] JSON.parse failed for area ${area.id} (${area.revierName}):`, e);
      console.error(`[Jagd] geom preview:`, geom.slice(0, 200));
      return;
    }
    console.log(`[Jagd] rendering area ${area.id} (${area.revierName}), geom type:`, geoJsonObj?.type);

    const color = area.typ?.toLowerCase().includes('eigen') ? '#7c3aed' : '#16a34a';

    const layer = L.geoJSON(geoJsonObj, {
      style: { color, weight: 2, opacity: 0.9, fillColor: color, fillOpacity: 0.15 },
    }).bindTooltip(area.revierName ?? String(area.id), { sticky: true });

    console.log(`[Jagd] layer bounds:`, layer.getBounds());
    this.map.addLayer(layer);
    this.jagdLayersMap.set(area.id, [layer]);
  }

  private renderJagdgebiet(area: Jagdgebiet): void {
    // Used for restoring state on init — geom is fetched lazily
    this.shownJagdIds.add(area.id);
    this.svc.getJagdgebietGeom(area.id).subscribe({
      next: geom => this.renderJagdgebietGeom(area, geom),
      error: () => { this.shownJagdIds.delete(area.id); },
    });
  }

  showAllJagd(): void {
    if (!this.jagdLoaded) {
      return;
    }
    for (const area of this.jagdgebiete) {
      if (!this.shownJagdIds.has(area.id)) {
        this.renderJagdgebiet(area);
      }
    }
    this.saveLayerState();
  }

  hideAllJagd(): void {
    for (const id of [...this.shownJagdIds]) {
      const layers = this.jagdLayersMap.get(id) ?? [];
      layers.forEach(l => this.map?.removeLayer(l));
      this.jagdLayersMap.delete(id);
    }
    this.shownJagdIds.clear();
    this.saveLayerState();
  }

  findJagdgebiet(): void {
    if (this.findingJagdgebiet) {
      return;
    }
    this.findingJagdgebiet = true;
    this.closeOnMobile();
    if (this.map) {
      this.map.getContainer().style.cursor = 'crosshair';
    }
  }

  private finishFindJagdgebiet(lat: number, lon: number): void {
    this.findingJagdgebiet = false;
    if (this.map) {
      this.map.getContainer().style.cursor = '';
    }
    this.svc.findJagdgebiet(lat, lon).subscribe({
      next: jagdgebietId => {
        if (jagdgebietId == null) {
          return;
        }
        const area = this.jagdgebiete.find(a => a.id === jagdgebietId);
        if (!area) {
          return;
        }
        if (!this.shownJagdIds.has(area.id)) {
          this.shownJagdIds.add(area.id);
          this.svc.getJagdgebietGeom(area.id).subscribe({
            next: geom => this.renderJagdgebietGeom(area, geom),
            error: () => { this.shownJagdIds.delete(area.id); },
          });
        }
        this.jumpToJagdgebiet(area);
      },
      error: () => { /* silently fail */ },
    });
  }

  jumpToJagdgebiet(area: Jagdgebiet): void {
    if (!this.map) {
      return;
    }
    const layers = this.jagdLayersMap.get(area.id);
    if (!layers || layers.length === 0) {
      return;
    }
    const geoLayer = layers[0] as L.GeoJSON;
    if (geoLayer.getBounds) {
      const bounds = geoLayer.getBounds();
      if (bounds.isValid()) {
        this.map.fitBounds(bounds, { padding: [30, 30] });
        this.closeOnMobile();
      }
    }
  }

  formatHa(ha: number | null): string {
    if (ha == null) {
      return '—';
    }
    return ha >= 100 ? `${Math.round(ha)} ha` : `${ha.toFixed(1)} ha`;
  }

  // ── Coverage ──────────────────────────────────────────────────────────────

  private loadCoverage(): void {
    if (this.loadingCoverage || this.coverageLoaded) {
      return;
    }
    this.loadingCoverage = true;
    this.svc.getCoverage().subscribe({
      next: tracks => {
        this.coverageTrackCount = tracks.length;
        this.rawCoverageTracks  = tracks;
        this.coverageCells = this.buildCoverageCells(tracks);
        this.loadingCoverage = false;
        this.coverageLoaded = true;
        this.renderCoverageOverlay();
        // If coverage was restored as visible but the overlay was just created with opacity 1,
        // ensure it respects coverageVisible
        if (this.coverageOverlay) {
          this.coverageOverlay.setOpacity(this.coverageVisible ? 1 : 0);
        }
      },
      error: () => { this.loadingCoverage = false; },
    });
  }

  /**
   * Kernel density estimation with linear falloff over coverageKernelRadius + temporal decay.
   */
  private buildCoverageCells(
    tracks: CoverageTrack[],
  ): Array<{ lat: number; lon: number; intensity: number }> {
    const CELL     = 0.0005;   // ~50 m at mid-latitudes
    const RADIUS_M = this.coverageKernelRadius;
    const LAT_M    = 111_000;  // metres per degree latitude (constant)
    const now      = Date.now();

    const counts = new Map<string, number>();
    const add = (iLat: number, iLon: number, v: number) => {
      const k = `${iLat},${iLon}`;
      counts.set(k, (counts.get(k) ?? 0) + v);
    };

    for (const track of tracks) {
      const ageDays = track.date
        ? (now - new Date(track.date).getTime()) / 86_400_000
        : 0;
      const w = Math.exp((-Math.LN2 * ageDays) / (this.coverageHalfLife * 30.44));

      for (const pt of track.points) {
        const lonM     = LAT_M * Math.cos(pt.lat * Math.PI / 180);
        const latCells = Math.ceil(RADIUS_M / (CELL * LAT_M));
        const lonCells = Math.ceil(RADIUS_M / (CELL * lonM));

        const ptILat = Math.floor(pt.lat / CELL);
        const ptILon = Math.floor(pt.lon / CELL);

        for (let di = -latCells; di <= latCells; di++) {
          for (let dj = -lonCells; dj <= lonCells; dj++) {
            const cILat = ptILat + di;
            const cILon = ptILon + dj;
            const cellCenterLat = (cILat + 0.5) * CELL;
            const cellCenterLon = (cILon + 0.5) * CELL;
            const dLatM = (cellCenterLat - pt.lat) * LAT_M;
            const dLonM = (cellCenterLon - pt.lon) * lonM;
            const dist  = Math.sqrt(dLatM * dLatM + dLonM * dLonM);
            if (dist < RADIUS_M) {
              add(cILat, cILon, w * (1 - dist / RADIUS_M));
            }
          }
        }
      }
    }

    const allCounts = Array.from(counts.values()).sort((a, b) => a - b);
    const p95       = allCounts[Math.floor(allCounts.length * 0.95)] ?? 1;
    const logNorm   = Math.log(p95 + 1);

    return Array.from(counts.entries()).map(([key, count]) => {
      const [iLat, iLon] = key.split(',').map(Number);
      return {
        lat:       iLat * CELL,
        lon:       iLon * CELL,
        intensity: Math.min(1, Math.log(count + 1) / logNorm),
      };
    });
  }

  private loadCoverageSettings(): void {
    try {
      const raw = localStorage.getItem('coverage-settings');
      if (!raw) {
        return;
      }
      const s = JSON.parse(raw);
      if (s.contrast != null) {
        this.coverageContrast = s.contrast;
      }
      if (s.minBrightness != null) {
        this.coverageMinBrightness = s.minBrightness;
      }
      if (s.halfLife != null) {
        this.coverageHalfLife = s.halfLife;
      }
      if (s.kernelRadius != null) {
        this.coverageKernelRadius = s.kernelRadius;
      }
      if (s.palette != null) {
        this.coveragePalette = s.palette;
      }
    } catch { /* ignore */ }
  }

  private saveCoverageSettings(): void {
    localStorage.setItem('coverage-settings', JSON.stringify({
      contrast:      this.coverageContrast,
      minBrightness: this.coverageMinBrightness,
      halfLife:      this.coverageHalfLife,
      kernelRadius:  this.coverageKernelRadius,
      palette:       this.coveragePalette,
    }));
  }

  onCoveragePaletteChange(event: Event): void {
    this.coveragePalette = (event.target as HTMLSelectElement).value;
    this.saveCoverageSettings();
    if (this.coverageLoaded) {
      this.renderCoverageOverlay();
    }
  }

  // Re-render only (contrast + minBrightness) — fast, no recompute needed
  onCoverageRenderChange(event: Event, field: 'contrast' | 'minBrightness'): void {
    const v = +(event.target as HTMLInputElement).value;
    if (field === 'contrast') {
      this.coverageContrast = v;
    }
    if (field === 'minBrightness') {
      this.coverageMinBrightness = v;
    }
    this.saveCoverageSettings();
    if (this.coverageLoaded) {
      this.renderCoverageOverlay();
    }
  }

  // Full recompute (halfLife + kernelRadius) — fires on mouse-up only
  onCoverageRecomputeChange(event: Event, field: 'halfLife' | 'kernelRadius'): void {
    const v = +(event.target as HTMLInputElement).value;
    if (field === 'halfLife') {
      this.coverageHalfLife = v;
    }
    if (field === 'kernelRadius') {
      this.coverageKernelRadius = v;
    }
    this.saveCoverageSettings();
    if (!this.coverageLoaded) {
      return;
    }
    this.coverageCells = this.buildCoverageCells(this.rawCoverageTracks);
    this.renderCoverageOverlay();
  }

  toggleCoverageVisibility(): void {
    this.coverageVisible = !this.coverageVisible;
    if (this.coverageOverlay) {
      this.coverageOverlay.setOpacity(this.coverageVisible ? 1 : 0);
    }
    this.saveLayerState();
  }

  private renderCoverageOverlay(): void {
    if (!this.map || this.coverageCells.length === 0) {
      return;
    }

    // ── Determine image bounds from the 95%-weight core ───────────────────────
    const CELL = 0.0005;
    const sorted = [...this.coverageCells].sort((a, b) => b.intensity - a.intensity);
    const totalWeight = sorted.reduce((s, c) => s + c.intensity, 0);
    let accumulated = 0;
    const core: typeof sorted = [];
    for (const cell of sorted) {
      core.push(cell);
      accumulated += cell.intensity;
      if (accumulated >= totalWeight * 0.95) {
        break;
      }
    }

    const coreLats = core.map(c => c.lat);
    const coreLons = core.map(c => c.lon);
    const imgMinLat = Math.min(...coreLats);
    const imgMaxLat = Math.max(...coreLats) + CELL;
    const imgMinLon = Math.min(...coreLons);
    const imgMaxLon = Math.max(...coreLons) + CELL;

    const imgWidth  = Math.round((imgMaxLon - imgMinLon) / CELL);
    const imgHeight = Math.round((imgMaxLat - imgMinLat) / CELL);

    // ── Render all cells into a pixel buffer (fast: one putImageData call) ───
    const offscreen = document.createElement('canvas');
    offscreen.width  = imgWidth;
    offscreen.height = imgHeight;
    const ctx = offscreen.getContext('2d');
    if (!ctx) {
      return;
    }
    const buf = ctx.createImageData(imgWidth, imgHeight);

    for (const cell of this.coverageCells) {
      const px =              Math.round((cell.lon - imgMinLon) / CELL);
      const py = imgHeight - 1 - Math.round((cell.lat - imgMinLat) / CELL);
      if (px < 0 || px >= imgWidth || py < 0 || py >= imgHeight) {
        continue;
      }
      const [r, g, b, a] = this.coverageColorRgba(Math.pow(cell.intensity, this.coverageContrast));
      const i = (py * imgWidth + px) * 4;
      buf.data[i]     = r;
      buf.data[i + 1] = g;
      buf.data[i + 2] = b;
      buf.data[i + 3] = a;
    }
    ctx.putImageData(buf, 0, 0);

    // ── Blur onto final canvas to smooth staircase edges ──────────────────────
    const final = document.createElement('canvas');
    final.width  = imgWidth;
    final.height = imgHeight;
    const fCtx = final.getContext('2d');
    if (!fCtx) {
      return;
    }
    fCtx.filter = 'blur(1.5px)';
    fCtx.drawImage(offscreen, 0, 0);

    // ── Hand the image to Leaflet ─────────────────────────────────────────────
    if (this.coverageOverlay) {
      this.map.removeLayer(this.coverageOverlay);
      this.coverageOverlay = null;
    }
    this.coverageOverlay = L.imageOverlay(
      final.toDataURL(),
      [[imgMinLat, imgMinLon], [imgMaxLat, imgMaxLon]],
      { opacity: this.coverageVisible ? 1 : 0, interactive: false },
    ).addTo(this.map);
  }

  /** Returns [r, g, b, a] in 0–255 range for direct ImageData writes. */
  private coverageColorRgba(t: number): [number, number, number, number] {
    const stops = this.COVERAGE_PALETTES[this.coveragePalette] ?? this.COVERAGE_PALETTES['plasma'];
    let lo = stops[0], hi = stops[stops.length - 1];
    for (let i = 0; i < stops.length - 1; i++) {
      if (t <= stops[i + 1][0]) {
        lo = stops[i];
        hi = stops[i + 1];
        break;
      }
    }
    const f = (t - lo[0]) / (hi[0] - lo[0]);
    const r = Math.round(lo[1][0] + f * (hi[1][0] - lo[1][0]));
    const g = Math.round(lo[1][1] + f * (hi[1][1] - lo[1][1]));
    const b = Math.round(lo[1][2] + f * (hi[1][2] - lo[1][2]));
    const a = Math.round((this.coverageMinBrightness + t * (0.85 - this.coverageMinBrightness)) * 255);
    return [r, g, b, a];
  }

  private detachCoverageOverlay(): void {
    if (this.coverageOverlay && this.map) {
      this.map.removeLayer(this.coverageOverlay);
      this.coverageOverlay = null;
    }
  }

  // ── Notes ────────────────────────────────────────────────────────────────

  private loadNotes(): void {
    if (this.notesLoaded) {
      return;
    }
    this.loadingNotes = true;
    this.svc.listNotes().subscribe({
      next: notes => {
        this.notes = notes;
        this.notesLoaded = true;
        this.renderNoteMarkers();
        this.loadingNotes = false;
      },
      error: () => { this.loadingNotes = false; },
    });
  }

  toggleNotesVisible(): void {
    this.notesVisible = !this.notesVisible;
    if (this.notesVisible && !this.notesLoaded) {
      this.loadNotes();
    } else {
      this.noteMarkers.forEach(m => {
        if (this.notesVisible) {
          this.map?.addLayer(m);
        } else {
          this.map?.removeLayer(m);
        }
      });
    }
    this.saveLayerState();
  }

  private renderNoteMarkers(): void {
    const map = this.map;
    if (!map) {
      return;
    }
    this.noteMarkers.forEach(m => map.removeLayer(m));
    this.noteMarkers.clear();

    for (const note of this.notes) {
      if (note.id == null) {
        continue;
      }
      const marker = L.marker(L.latLng(note.lat, note.lon), { icon: this.noteIcon() });
      marker.bindPopup(`<strong>${escapeHtml(note.title)}</strong><br>${escapeHtml(note.body ?? '')}`);
      this.noteMarkers.set(note.id, marker);
      if (this.notesVisible) {
        this.map.addLayer(marker);
      }
    }
  }

  get hasGeolocation(): boolean { return 'geolocation' in navigator; }

  startAddNote(): void {
    this.addingNote = true;
    if (this.map) {
      this.map.getContainer().style.cursor = 'crosshair';
    }
  }

  placeNoteHere(): void {
    const open = (lat: number, lon: number) => {
      if (!this.notesVisible) {
        this.toggleNotesVisible();
      }
      this.editingNote = { id: null, lat, lon, title: '', body: null, createdAt: null };
    };
    if (this.lastKnownPosition) {
      open(this.lastKnownPosition.latitude, this.lastKnownPosition.longitude);
    } else {
      navigator.geolocation.getCurrentPosition(
        pos => this.zone.run(() => open(pos.coords.latitude, pos.coords.longitude)),
        ()  => this.zone.run(() => { this.locationError = 'Location unavailable'; }),
        { enableHighAccuracy: true, timeout: 10000 },
      );
    }
  }

  private onMapClick(e: L.LeafletMouseEvent): void {
    if (this.addingNote) {
      this.addingNote = false;
      if (this.map) {
        this.map.getContainer().style.cursor = '';
      }
      this.editingNote = {
        id: null,
        lat: e.latlng.lat,
        lon: e.latlng.lng,
        title: '',
        body: null,
        createdAt: null,
      };
      return;
    }

    if (this.findingJagdgebiet) {
      this.finishFindJagdgebiet(e.latlng.lat, e.latlng.lng);
      return;
    }

    if (this.editingPhotoPosition) {
      const photo = this.editingPhotoPosition;
      this.editingPhotoPosition = null;
      if (this.map) {
        this.map.getContainer().style.cursor = '';
      }
      this.svc.updatePhotoPosition(photo.uid, e.latlng.lat, e.latlng.lng).subscribe({
        next: () => {
          const idx = this.photos.findIndex(p => p.uid === photo.uid);
          if (idx >= 0) {
            this.photos[idx] = { ...this.photos[idx], lat: e.latlng.lat, lon: e.latlng.lng };
          }
          this.renderPhotoMarkers();
        },
      });
    }
  }

  saveNote(): void {
    if (!this.editingNote || !this.editingNote.title.trim()) {
      return;
    }
    if (this.editingNote.id != null) {
      this.svc.updateNote(this.editingNote.id, this.editingNote).subscribe({
        next: updated => {
          const idx = this.notes.findIndex(n => n.id === updated.id);
          if (idx >= 0) {
            this.notes[idx] = updated;
          }
          this.renderNoteMarkers();
          this.editingNote = null;
        },
      });
    } else {
      this.svc.createNote(this.editingNote).subscribe({
        next: created => {
          this.notes.push(created);
          this.renderNoteMarkers();
          this.editingNote = null;
        },
      });
    }
  }

  private closeOnMobile(): void {
    if (this.panelOpen && window.innerWidth < 768) {
      this.togglePanel();
    }
  }

  jumpToNote(note: OutdoorNote): void {
    if (!this.map || note.id == null) {
      return;
    }
    this.closeOnMobile();
    this.map.flyTo(L.latLng(note.lat, note.lon), Math.max(this.map.getZoom(), 16), { duration: 0.6 });
    const marker = this.noteMarkers.get(note.id);
    if (marker) {
      setTimeout(() => marker.openPopup(), 650);
    }
  }

  editNote(note: OutdoorNote): void {
    this.editingNote = { ...note };
  }

  deleteNote(note: OutdoorNote): void {
    if (note.id == null) {
      return;
    }
    this.svc.deleteNote(note.id).subscribe({
      next: () => {
        this.notes = this.notes.filter(n => n.id !== note.id);
        const m = this.noteMarkers.get(note.id);
        if (m && this.map) {
          this.map.removeLayer(m);
        }
        this.noteMarkers.delete(note.id);
      },
    });
  }

  cancelEdit(): void {
    this.editingNote = null;
    this.addingNote = false;
    this.editingPhotoPosition = null;
    if (this.map) {
      this.map.getContainer().style.cursor = '';
    }
  }

  // ── Photos ────────────────────────────────────────────────────────────────

  private loadPhotos(): void {
    if (this.photosLoaded) {
      return;
    }
    this.loadingPhotos = true;
    this.svc.listPhotos().subscribe({
      next: photos => {
        this.photos = photos;
        this.photosLoaded = true;
        this.renderPhotoMarkers();
        this.loadingPhotos = false;
      },
      error: () => { this.loadingPhotos = false; },
    });
  }

  togglePhotosVisible(): void {
    this.photosVisible = !this.photosVisible;
    if (this.photosVisible && !this.photosLoaded) {
      this.loadPhotos();
    } else if (this.photoClusterGroup) {
      if (this.photosVisible) {
        this.map?.addLayer(this.photoClusterGroup);
      } else {
        this.map?.removeLayer(this.photoClusterGroup);
      }
    }
    this.saveLayerState();
  }

  private renderPhotoMarkers(): void {
    if (!this.map) {
      return;
    }

    // tear down previous cluster group and blob URLs
    if (this.photoClusterGroup) {
      this.map.removeLayer(this.photoClusterGroup);
      this.photoClusterGroup = null;
    }
    this.photoBlobUrls.forEach(u => URL.revokeObjectURL(u));
    this.photoBlobUrls = [];

    // Get markerClusterGroup function from L or window.L
    const win = window as unknown as { L?: typeof L };
    const markerClusterGroupFn = L.markerClusterGroup ?? win.L?.markerClusterGroup;
    if (!markerClusterGroupFn) {
      console.error('markerClusterGroup is not available on L or window.L');
      return;
    }

    const cluster = markerClusterGroupFn({
      maxClusterRadius: 60,
      showCoverageOnHover: false,
      iconCreateFunction: c => L.divIcon({
        html: `<div class="photo-cluster">${c.getChildCount()}</div>`,
        className: '',
        iconSize: [40, 40],
      }),
    });

    const pinIcon = L.divIcon({
      className: '',
      html: `<div style="width:10px;height:10px;border-radius:50%;background:#2563eb;border:2px solid #fff;box-shadow:0 1px 3px rgba(0,0,0,.4)"></div>`,
      iconSize: [10, 10],
      iconAnchor: [5, 5],
    });

    for (const photo of this.photos) {
      const m = L.marker(L.latLng(photo.lat, photo.lon), { icon: pinIcon });

      m.bindPopup('<div class="photo-popup-loading">Loading…</div>', { minWidth: 220 });
      m.on('popupopen', () => {
        if (!this.map) {
          return;
        }
        const popup = m.getPopup();
        if (!popup) {
          return;
        }
        if (!popup.getContent()?.toString().includes('photo-popup-loading')) {
          return;
        }

        const uid = photo.uid;
        const dateStr = this.formatTakenAt(photo.takenAt);
        const titleHtml = photo.title ? `<div style="font-size:12px;margin-top:2px">${escapeHtml(photo.title)}</div>` : '';

        const popupContent = [
          `<div id="photo-thumb-${uid}" class="photo-popup-loading">Loading…</div>`,
          `<div style="font-size:11px;color:#475569;margin-top:4px">${dateStr}</div>`,
          titleHtml,
          '<div style="margin-top:6px;display:flex;gap:6px">',
          `<button id="photo-full-btn-${uid}" style="font-size:11px;cursor:pointer;padding:2px 6px;border:1px solid #94a3b8;border-radius:3px;background:#f8fafc">Full size</button>`,
          `<button id="photo-edit-btn-${uid}" style="font-size:11px;cursor:pointer;padding:2px 6px;border:1px solid #94a3b8;border-radius:3px;background:#f8fafc">Edit position</button>`,
          '</div>',
        ].join('');
        popup.setContent(popupContent);

        setTimeout(() => {
          const fullBtn = document.getElementById(`photo-full-btn-${uid}`);
          if (fullBtn) {
            fullBtn.onclick = () => this.zone.run(() => this.openPhotoViewer(photo));
          }
          const editBtn = document.getElementById(`photo-edit-btn-${uid}`);
          if (editBtn) {
            editBtn.onclick = () => this.zone.run(() => {
              this.editingPhotoPosition = photo;
              if (this.map) {
                this.map.closePopup();
                this.map.getContainer().style.cursor = 'crosshair';
              }
            });
          }
        }, 0);

        this.svc.photoThumbBlobUrl(photo.hash, photo.previewToken).subscribe({
          next: blobUrl => {
            this.photoBlobUrls.push(blobUrl);
            const thumbDiv = document.getElementById(`photo-thumb-${uid}`);
            if (thumbDiv) {
              thumbDiv.className = '';
              thumbDiv.innerHTML = `<img src="${blobUrl}" style="max-width:200px;display:block;border-radius:4px">`;
            }
          },
          error: () => {
            const thumbDiv = document.getElementById(`photo-thumb-${uid}`);
            if (thumbDiv) {
              thumbDiv.innerHTML = `<span style="color:#dc2626;font-size:12px">Failed to load photo</span>`;
            }
          },
        });
      });

      cluster.addLayer(m);
    }

    this.photoClusterGroup = cluster;
      if (this.photosVisible) {
        this.map.addLayer(cluster);
      }
  }

  openPhotoViewer(photo: Photo): void {
    this.viewingPhoto = photo;
    this.viewingPhotoLoading = true;
    this.viewingPhotoBlobUrl = null;
    if (this.map) {
      this.map.closePopup();
    }
    this.svc.photoFullBlobUrl(photo.hash, photo.previewToken).subscribe({
      next: blobUrl => {
        this.viewingPhotoBlobUrl = blobUrl;
        this.viewingPhotoLoading = false;
      },
      error: () => { this.viewingPhotoLoading = false; },
    });
  }

  closePhotoViewer(): void {
    if (this.viewingPhotoBlobUrl) {
      URL.revokeObjectURL(this.viewingPhotoBlobUrl);
    }
    this.viewingPhoto = null;
    this.viewingPhotoBlobUrl = null;
    this.viewingPhotoLoading = false;
  }

  // ── Helpers ───────────────────────────────────────────────────────────────

  /** O(n) nearest-point lookup by squared lat/lon distance. */
  private findNearestPointTime(points: TrackPoint[], latlng: L.LatLng): number | null {
    if (points.length === 0) {
      return null;
    }
    let bestDist = Infinity;
    let bestTimeMs = 0;
    for (const p of points) {
      const dlat = p.lat - latlng.lat;
      const dlon = p.lon - latlng.lng;
      const d = dlat * dlat + dlon * dlon;
      if (d < bestDist) {
        bestDist = d;
        bestTimeMs = new Date(p.time).getTime();
      }
    }
    return bestTimeMs;
  }

  private formatPointTime(timeMs: number): string {
    return new Intl.DateTimeFormat('de-AT', {
      timeZone: 'Europe/Vienna',
      hour: '2-digit', minute: '2-digit', second: '2-digit',
      hour12: false,
    }).format(new Date(timeMs));
  }

  private dotIcon(color: string, radius: number): L.DivIcon {
    return L.divIcon({
      className: '',
      html: `<div style="width:${radius * 2}px;height:${radius * 2}px;border-radius:50%;background:${color};border:2px solid #fff;box-shadow:0 1px 3px rgba(0,0,0,.4)"></div>`,
      iconSize: [radius * 2, radius * 2],
      iconAnchor: [radius, radius],
    });
  }

  private noteIcon(): L.DivIcon {
    return L.divIcon({
      className: '',
      html: `<div style="width:28px;height:28px;border-radius:50% 50% 50% 0;transform:rotate(-45deg);background:#f59e0b;border:2px solid #fff;box-shadow:0 1px 3px rgba(0,0,0,.4)"></div>`,
      iconSize: [28, 28],
      iconAnchor: [14, 28],
    });
  }

  // ── Template helpers ──────────────────────────────────────────────────────

  private formatTakenAt(takenAt: string | null): string {
    if (!takenAt) {
      return '';
    }
    try {
      return new Intl.DateTimeFormat('sv-SE', {
        timeZone: 'Europe/Vienna',
        year: 'numeric', month: '2-digit', day: '2-digit',
        hour: '2-digit', minute: '2-digit', second: '2-digit',
        hour12: false,
      }).format(new Date(takenAt));
    } catch {
      return takenAt;
    }
  }

  formatDuration(minutes: number | null): string {
    if (minutes == null) {
      return '—';
    }
    const h = Math.floor(minutes / 60);
    const m = minutes % 60;
    return h > 0 ? `${h}h ${m}m` : `${m}m`;
  }

  formatDist(km: number | null): string {
    if (km == null) {
      return '—';
    }
    return km >= 1 ? `${km.toFixed(1)} km` : `${Math.round(km * 1000)} m`;
  }
}
